import React from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPinIcon,
  StarIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  GiftIcon,
  CheckIcon,
  XMarkIcon,
  BoltIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

import * as API from '../../../api';
import EventCard from '../../../shared/components/EventCard';
import useHorizontalScroller from '../../../shared/hooks/useHorizontalScroller';
import useVideoEmbeds from '../../../shared/hooks/useVideoEmbeds';
import EventGallery from './EventGallery';
import EventNotFound from './EventNotFound';
import Gift from './gift';
import Program from './program';

// ---- helpers ---------------------------------------------------------------

function defaultPrice(prices = []) {
  if (!prices.length) return null;
  const def = prices.find((p) => p.default === 1 || p.default === '1');
  return (def || prices[0]).price;
}
function priceDescription(prices = []) {
  const def = prices.find((p) => p.default === 1 || p.default === '1');
  return def?.description || '';
}
// Same display rule as EventCard: any reviews → 4.9, else null.
function ratingValue(event) {
  return event?.total_reviews ? '4.9' : null;
}
function typeLabel(type, t) {
  if (type === 'Activitie') return t('detail.typeActivitie');
  if (type === 'Sejour') return t('detail.typeSejour');
  return t('detail.typeEvent');
}

// ---- small section primitives ---------------------------------------------

function Section({ title, children }) {
  return (
    <section className="border-t border-slate-100 py-7">
      {title ? (
        <h2 className="mb-4 font-display text-xl font-semibold text-ink">{title}</h2>
      ) : null}
      {children}
    </section>
  );
}

function ListWithIcons({ items, positive = true }) {
  const Icon = positive ? CheckIcon : XMarkIcon;
  return (
    <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-ink-muted">
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
              positive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'
            }`}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
          <span>{typeof it === 'string' ? it : it.included ?? it.not_included ?? it.name}</span>
        </li>
      ))}
    </ul>
  );
}

// ---- booking card ----------------------------------------------------------

function BookingCard({ event, price, priceDesc, onBook }) {
  const { t } = useTranslation();
  const isPartner =
    Object.keys(API.AuthStorage).length && API.AuthStorage?.user?.type === 'partner';
  const disabled = isPartner || event.expire;
  const label = event.expire
    ? t('detail.bookExpired')
    : event.type === 'Sejour'
    ? t('detail.bookCtaSejour')
    : t('detail.bookCta');

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-xs font-semibold text-accent-600">
        <ShieldCheckIcon className="h-3.5 w-3.5" />
        {t('detail.bookBestPrice')}
      </span>

      <div className="mt-4 flex items-end gap-1.5">
        <span className="text-xs text-ink-subtle">{t('detail.bookFrom')}</span>
      </div>
      <p className="font-display text-3xl font-bold leading-none text-brand-500">
        {price != null ? (
          <>
            <bdi>{price}</bdi> {t('home.featuredCurrency', 'MAD')}
          </>
        ) : (
          '—'
        )}
      </p>
      <p className="mt-1 text-xs text-ink-subtle">
        {t('detail.bookPerPerson')}
        {priceDesc ? ` · ${priceDesc}` : ''}
      </p>

      <ul className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm text-ink-muted">
        <li className="flex items-center gap-2.5">
          <BoltIcon className="h-4 w-4 text-accent-500" />
          {t('detail.bookInstant')}
        </li>
        <li className="flex items-center gap-2.5">
          <ClockIcon className="h-4 w-4 text-brand-500" />
          {t('detail.bookFlexible')}
        </li>
        <li className="flex items-center gap-2.5">
          <ShieldCheckIcon className="h-4 w-4 text-emerald-500" />
          {t('detail.bookFreeCancel')}
        </li>
      </ul>

      <button
        type="button"
        onClick={onBook}
        disabled={disabled}
        className="mt-5 w-full rounded-xl bg-accent-500 px-4 py-3.5 text-center text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-accent-500"
      >
        {label}
      </button>
    </div>
  );
}

// ---- related carousel ------------------------------------------------------

function RelatedCarousel({ event }) {
  const { t } = useTranslation();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { scrollerRef } = useHorizontalScroller([items.length, loading]);

  React.useEffect(() => {
    let mounted = true;
    API.Events({ type: event.type, page: 1, per_page: 10 })
      .then((res) => {
        if (!mounted) return;
        setItems((res?.data || []).filter((e) => e.id !== event.id).slice(0, 8));
      })
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [event.type, event.id]);

  if (!loading && !items.length) return null;

  const titleKey =
    event.type === 'Activitie'
      ? 'detail.relatedActivities'
      : event.type === 'Sejour'
      ? 'detail.relatedStays'
      : 'detail.relatedTrips';

  return (
    <section className="bg-surface-muted py-12">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        <h2 className="mb-6 font-display text-2xl font-semibold text-ink">{t(titleKey)}</h2>
        <div
          ref={scrollerRef}
          className="flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card"
                  style={{ width: '288px' }}
                >
                  <div className="aspect-[4/3] animate-pulse bg-slate-100" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
                  </div>
                </div>
              ))
            : items.map((e) => <EventCard key={e.id} event={e} />)}
        </div>
      </div>
    </section>
  );
}

// ---- page ------------------------------------------------------------------

export default function EventDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [event, setEvent] = React.useState(null);
  const [organizer, setOrganizer] = React.useState(null);
  const [giftOpen, setGiftOpen] = React.useState(false);
  const descriptionRef = React.useRef(null);

  // Post-process CMS-authored description HTML after it mounts: replace any
  // raw video <iframe> embeds with a click-to-play placeholder (see
  // shared/hooks/useVideoEmbeds.js for why). Must run before the early
  // returns below to satisfy the Rules of Hooks. `loading` is included so
  // the effect re-fires on the render where the skeleton is swapped for
  // the real description markup — until then descriptionRef.current is
  // null (or pointed at the skeleton), even though event/id already
  // changed on an earlier render.
  useVideoEmbeds(descriptionRef, [
    loading,
    event?.id,
    event?.full_description,
    event?.description,
  ]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    setEvent(null);
    setOrganizer(null);
    window.scrollTo({ top: 0 });

    API.Event(id)
      .then((res) => {
        if (!mounted) return;
        const ev = res.data;
        setEvent(ev);
        document.title = `${API.APP_NAME} - ${ev.title}`;
        // Canonical → the id-based URL (matches the links Google indexed).
        setCanonical(`${window.location.origin}/event/${ev.id}`);
        // The booking flow expects the next page to read /event/{id} as intended.
        localStorage.setItem('intended', `/event/${id}`);
        if (ev.proprietor === 'partner' && ev.partner_id) {
          API.PartnerInformation(ev.partner_id)
            .then((r) => mounted && setOrganizer(r.data))
            .catch(() => {});
        }
      })
      .catch(() => mounted && setNotFound(true))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
      // Reset to the site root canonical when leaving the page.
      setCanonical(`${window.location.origin}/`);
    };
  }, [id]);

  // Booking handoff — unchanged behaviour: stash the event, drop any gift,
  // then route to /booking.
  const handleBook = async () => {
    await localStorage.removeItem('gift');
    await localStorage.setItem('order', JSON.stringify(event));
    history.push('/booking');
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-10 sm:px-6 lg:px-8">
        <div className="h-[300px] w-full animate-pulse rounded-2xl bg-slate-100 sm:h-[440px]" />
        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            <div className="h-8 w-2/3 animate-pulse rounded bg-slate-100" />
            <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
            <div className="h-40 w-full animate-pulse rounded bg-slate-100" />
          </div>
          <div className="h-72 w-full animate-pulse rounded-2xl bg-slate-100" />
        </div>
      </div>
    );
  }

  if (notFound || !event) return <EventNotFound />;

  const price = defaultPrice(event.prices);
  const priceDesc = priceDescription(event.prices);
  const stars = ratingValue(event);
  const reviewsN = event.total_reviews || 0;
  const gallery = (event.gallery || []).concat(
    event.image ? [{ id: 'cover', name: event.title, pathImage: event.image }] : []
  );
  const gatheringLabel =
    event.type === 'Activitie' || event.type === 'Sejour'
      ? event.citie
      : (event.gathering || []).map((g) => g.gathering).join(' · ') || event.citie;

  const included = event.included || [];
  const notIncluded = event.not_included || [];
  const strongPoints = event.strong_point || [];
  const abouts = event.abouts || [];
  const programmes = event.programmes || [];

  return (
    <div className="bg-surface pb-20 lg:pb-0">
      <div className="mx-auto w-full max-w-container px-4 pt-5 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-ink-subtle" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-brand-500">
            {t('detail.breadcrumbHome')}
          </Link>
          <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
          <Link to="/events" className="hover:text-brand-500">
            {t('detail.breadcrumbEvents')}
          </Link>
          <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
          <span className="truncate text-ink-muted">{event.title}</span>
        </nav>

        {/* Title block */}
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
              {typeLabel(event.type, t)}
            </span>
            <h1 className="mt-2.5 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-[34px]">
              {event.title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-ink-muted">
              <span className="inline-flex items-center gap-1.5">
                <MapPinIcon className="h-4 w-4 text-brand-500" />
                {gatheringLabel}
              </span>
              {stars ? (
                <span className="inline-flex items-center gap-1.5">
                  <StarSolid className="h-4 w-4 text-accent-500" />
                  <span className="font-semibold text-ink">{stars}</span>
                  <span className="text-ink-subtle">
                    ({reviewsN} {t('detail.reviews')})
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          {!event.expire ? (
            <button
              type="button"
              onClick={() => setGiftOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-brand-500 hover:text-brand-500"
            >
              <GiftIcon className="h-4 w-4" />
              {t('detail.gift')}
            </button>
          ) : null}
        </div>

        {/* Gallery */}
        <div className="mt-5">
          <EventGallery images={gallery} title={event.title} />
        </div>

        {/* Two-column body */}
        <div className="mt-8 grid grid-cols-1 gap-10 pb-12 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* Left — content */}
          <div>
            {/* Quick facts (trips show duration/dates/capacity/age) */}
            {event.type === 'Event' ? (
              <div className="grid grid-cols-2 gap-4 rounded-2xl border border-slate-100 bg-surface-muted p-5 sm:grid-cols-4">
                <Fact icon={ClockIcon} label={`${event.total_days} ${t('detail.days')}`} />
                <Fact
                  icon={CalendarDaysIcon}
                  label={t('detail.fromTo', { from: event.date_depart, to: event.date_end })}
                />
                <Fact icon={UserGroupIcon} label={`${t('detail.capacity')} ${event.capacitie}`} />
                <Fact icon={StarIcon} label={`${t('detail.age')} ${event.age}+`} />
              </div>
            ) : null}

            {/* Description — render both the rich prose (full_description) and
                the intro block (description), which can hold a contact link and
                an embedded video. Embedded iframes are made responsive. */}
            {event.full_description || event.description ? (
              <Section title={t('detail.aboutTitle')}>
                <div
                  ref={descriptionRef}
                  className="prose prose-sm max-w-none text-ink-muted [&_a]:font-medium [&_a]:text-brand-500 [&_a:hover]:underline [&_h2]:text-ink [&_h3]:text-ink [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:max-w-xl [&_iframe]:rounded-xl [&_p]:my-2 [&_strong]:text-ink"
                  dangerouslySetInnerHTML={{
                    __html: [event.full_description, event.description]
                      .filter(Boolean)
                      .join(''),
                  }}
                />
              </Section>
            ) : null}

            {/* Highlights */}
            {strongPoints.length ? (
              <Section title={t('detail.highlightsTitle')}>
                <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {strongPoints.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-ink-muted">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <StarSolid className="h-3 w-3" />
                      </span>
                      <span>{typeof p === 'string' ? p : p.strong_point ?? p.name}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}

            {/* Included / not included */}
            {included.length ? (
              <Section title={t('detail.includedTitle')}>
                <ListWithIcons items={included} positive />
              </Section>
            ) : null}
            {notIncluded.length ? (
              <Section title={t('detail.notIncludedTitle')}>
                <ListWithIcons items={notIncluded} positive={false} />
              </Section>
            ) : null}

            {/* Programme (trips) */}
            {event.type === 'Event' && programmes.length ? (
              <Section title={t('detail.programmeTitle')}>
                <div className="space-y-2">
                  {programmes.map((p, i) => (
                    <Program key={i} title={p.title}>
                      <div dangerouslySetInnerHTML={{ __html: p.description }} />
                    </Program>
                  ))}
                </div>
              </Section>
            ) : null}

            {/* About items */}
            {abouts.length ? (
              <Section title={t('detail.aboutTitle')}>
                <div className="space-y-4">
                  {abouts.map((a) => (
                    <div key={a.id} className="flex gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
                        <CheckBadgeIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <h4 className="font-semibold text-ink">{a.title}</h4>
                        <p className="mt-0.5 text-sm text-ink-muted">{a.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            ) : null}

            {/* Before you go */}
            {event.before_description ? (
              <Section title={event.before_title || t('detail.beforeTitle')}>
                <p className="whitespace-pre-line text-sm text-ink-muted">
                  {event.before_description}
                </p>
              </Section>
            ) : null}

            {/* Organizer */}
            {event.proprietor !== 'admin' && organizer ? (
              <Section title={t('detail.organizerTitle')}>
                <div className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-surface-muted p-4">
                  {organizer.logo ? (
                    <img
                      src={organizer.logo}
                      alt={organizer.name}
                      className="h-14 w-14 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white">
                      {(organizer.name || '?').charAt(0)}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-semibold text-ink">{organizer.name}</h4>
                      <CheckBadgeIcon className="h-4 w-4 text-brand-500" />
                    </div>
                    <p className="text-xs text-ink-subtle">{t('detail.organizerVerified')}</p>
                    {organizer.about ? (
                      <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{organizer.about}</p>
                    ) : null}
                  </div>
                </div>
              </Section>
            ) : null}

            {/* Reviews */}
            {event.reviews?.length ? (
              <Section title={t('detail.reviewsTitle')}>
                <div className="space-y-4">
                  {event.reviews.map((r, i) => {
                    // The API nests the reviewer under `user` (an object with
                    // its own `.name`), not a flat `r.name`/`r.user` string —
                    // reading `r.user` directly as a display name crashed
                    // this whole page (`.charAt` called on an object).
                    const reviewerName =
                      (typeof r.name === 'string' && r.name) ||
                      (typeof r.user === 'string' && r.user) ||
                      (typeof r.user?.name === 'string' && r.user.name) ||
                      null;
                    return (
                    <div key={i} className="rounded-2xl border border-slate-100 p-4">
                      <div className="flex items-center gap-2">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 font-semibold text-brand-600">
                          {(reviewerName || 'A').charAt(0)}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ink">{reviewerName || '—'}</p>
                          <div className="flex">
                            {Array.from({ length: 5 }).map((_, s) => (
                              <StarSolid
                                key={s}
                                className={`h-3.5 w-3.5 ${
                                  s < (r.rating || 5) ? 'text-accent-500' : 'text-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      {r.comment ? (
                        <p className="mt-2.5 text-sm text-ink-muted">{r.comment}</p>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              </Section>
            ) : null}
          </div>

          {/* Right — sticky booking */}
          <aside>
            <div className="lg:sticky lg:top-24">
              <BookingCard event={event} price={price} priceDesc={priceDesc} onBook={handleBook} />
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky booking bar — extra right padding so the floating
          WhatsApp button never covers the CTA. */}
      <div className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-between gap-3 border-t border-slate-200 bg-white py-3 pl-4 pr-20 shadow-elevated lg:hidden">
        <div>
          <p className="text-[11px] text-ink-subtle">{t('detail.bookFrom')}</p>
          <p className="font-display text-lg font-bold leading-none text-brand-500">
            {price != null ? (
              <>
                <bdi>{price}</bdi> {t('home.featuredCurrency', 'MAD')}
              </>
            ) : (
              '—'
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={handleBook}
          disabled={
            (Object.keys(API.AuthStorage).length && API.AuthStorage?.user?.type === 'partner') ||
            event.expire
          }
          className="flex-1 rounded-xl bg-accent-500 px-4 py-3 text-center text-sm font-bold text-brand-900 shadow-card transition-colors hover:bg-accent-400 disabled:opacity-50"
        >
          {event.expire
            ? t('detail.bookExpired')
            : event.type === 'Sejour'
            ? t('detail.bookCtaSejour')
            : t('detail.bookCta')}
        </button>
      </div>

      {/* Related */}
      <RelatedCarousel event={event} />

      {/* Gift modal (legacy component, preserved) */}
      <Gift
        event={event}
        shown={giftOpen}
        close={() => setGiftOpen(false)}
        toRegister={() => setGiftOpen(false)}
      />
    </div>
  );
}

function Fact({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-brand-500 shadow-card">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-sm font-medium text-ink">{label}</span>
    </div>
  );
}

// Set/update the document's <link rel="canonical"> href (created if missing).
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}
